import { useCallback, useEffect, useRef, useState } from "react";
import type { FeedbackVote, ReasonCode, VoteDirection } from "@skills-marketplace/contracts/contract-8-feedback";
import type { MarketplaceEntry } from "../types/index";
import { castVote, fetchMyVote, reportVoteEvent, withdrawVote } from "../lib/feedback-client";
import { useCatalog } from "../lib/catalog-context";
import { reportError } from "../lib/newrelic";
import VoteBlock from "./VoteBlock";
import styles from "./VoteSection.module.css";

interface Props {
  plugin: MarketplaceEntry;
}

/**
 * Data container for the detail page's vote UI (#617, #618).
 *
 * Exists so PluginDetailPage stays prop-driven: all of the fetching, the write calls
 * and the error state live here, and the page adds one element. VoteBlock below is
 * purely presentational, so the split is container/presentational rather than an
 * arbitrary file boundary — VoteBlock is unit-tested with plain callbacks precisely
 * because it knows nothing about any of this.
 *
 * The tally comes from the catalog entry (joined by CatalogProvider), NOT from a
 * per-page fetch. That is what keeps the number here identical to the one on the
 * catalog card — one source, two surfaces. (List rows carry no usefulness badge;
 * #617 scopes it to cards.)
 */
export default function VoteSection({ plugin }: Props) {
  const { applyVoteResult } = useCatalog();
  const [myVote, setMyVote] = useState<FeedbackVote | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  /**
   * Monotonic id for the most recent write. A response is applied only if its own id
   * is still the latest — otherwise it is a SUPERSEDED write and must be dropped.
   *
   * VoteBlock fires onVote and resets its pending state synchronously, without
   * awaiting, so a voter can start a second write before the first resolves: pick
   * Useful, Submit, then change their mind to Not useful and Submit again. Nothing
   * makes HTTP responses arrive in send order, so the FIRST request can land last and
   * apply its result on top of the second — silently replacing the voter's actual last
   * action, on both the detail block and the card badge, until the next full reload.
   *
   * A write-vs-write race, and therefore NOT the one mergeTallies fixes: that guards a
   * bulk READ against a write, and both of these are writes on the same entry.
   * Raised in review on #571 (Jordan-Thomas).
   *
   * A ref, not state: it must be read and written without triggering a render, and
   * every consumer reads it after an await rather than during one.
   */
  const writeSeqRef = useRef(0);

  const installName = plugin.installName;

  // An entry with no installName is unvotable — there is no key to store a vote
  // against. Hide the affordance rather than offering a control that cannot work.
  const votable = !!installName;

  // The caller's prior vote, so a returning voter sees "You marked this useful" with
  // its Undo rather than being asked again. Non-throwing by design: not knowing the
  // prior vote degrades to showing the prompt, which is recoverable.
  useEffect(() => {
    if (!installName) return;
    let cancelled = false;

    setMyVote(null);
    setFailure(null);
    // Same hazard one more time, in the other direction: this read is slow (a Lambda
    // plus a DynamoDB get) and only cancels on unmount or an entry change, so a voter
    // who votes before it lands would have their fresh vote overwritten by the
    // pre-vote answer. Any write started since invalidates this read.
    const seqAtRead = writeSeqRef.current;
    void fetchMyVote(installName).then((vote) => {
      if (!cancelled && seqAtRead === writeSeqRef.current) setMyVote(vote);
    });

    return () => {
      cancelled = true;
    };
  }, [installName]);

  const handleVote = useCallback(
    async (direction: VoteDirection, reasonCodes: ReasonCode[], reasonText: string | null) => {
      if (!installName) return;
      setFailure(null);
      const seq = ++writeSeqRef.current;
      try {
        // ONE call carrying the vote and its reasons — see contract-8. The response
        // is authoritative, so nothing here computes a tally.
        const result = await castVote(installName, direction, reasonCodes, reasonText);
        // Superseded: the voter has since committed another vote on this entry, and
        // that later write owns the outcome. Dropping this one is the whole fix.
        if (seq !== writeSeqRef.current) return;
        applyVoteResult(installName, result.tally);
        setMyVote({
          direction,
          reasonCodes,
          reasonText,
          votedAt: new Date().toISOString(),
        });
        // Only after the write succeeded: instrumenting the click would count votes
        // that never landed (#617's primary metric).
        reportVoteEvent(installName, direction, reasonCodes.length, reasonText !== null);
      } catch (err) {
        // A failed vote must SAY so. Silently swallowing it would leave the voter
        // believing they were counted when they were not — worse than an error.
        //
        // Still reported, because the failure happened and is worth knowing about, but
        // the message is suppressed for a superseded write: telling someone their vote
        // failed when the vote they actually last cast succeeded is its own wrong answer.
        reportError(err, "cast-vote");
        if (seq !== writeSeqRef.current) return;
        setFailure("Your vote could not be saved. Please try again.");
      }
    },
    [installName, applyVoteResult],
  );

  const handleWithdraw = useCallback(async () => {
    if (!installName) return;
    setFailure(null);
    // Undo shares the counter with castVote on purpose: withdraw-then-revote (and
    // revote-then-withdraw) is the same race across two different endpoints, so one
    // sequence over all writes is what actually orders them.
    const seq = ++writeSeqRef.current;
    try {
      const result = await withdrawVote(installName);
      if (seq !== writeSeqRef.current) return;
      applyVoteResult(installName, result.tally);
      setMyVote(null);
    } catch (err) {
      reportError(err, "withdraw-vote");
      if (seq !== writeSeqRef.current) return;
      setFailure("Your vote could not be withdrawn. Please try again.");
    }
  }, [installName, applyVoteResult]);

  if (!votable) return null;

  return (
    <div>
      <VoteBlock
        tally={plugin.feedback}
        myVote={myVote}
        onVote={handleVote}
        onWithdraw={handleWithdraw}
      />
      {failure && (
        // Assertive: the voter has just acted and believes it worked, so this cannot
        // wait for them to happen to move focus here.
        <p className={styles.failure} role="alert">
          {failure}
        </p>
      )}
    </div>
  );
}
